import { ApplyOrgChartImportRequest } from '@interfaces/economic-groups/requests/apply-org-chart-import.request';
import {
  OrgChartNodeActions,
  OrgChartProposalNodeResponse,
  OrgChartProposalResponse,
} from '@interfaces/economic-groups/responses/org-chart-import.response';
import { ErrorResponse } from '@models/commom/error-response';

/**
 * Regras da tela de revisão da importação de organograma (card 3578, opção A): ordem da árvore, etiquetas, avisos,
 * contagem e o payload do apply. Funções puras, fora do componente. A conferência do CNPJ aqui é só para mostrar o erro
 * na tela; quem decide no apply é o backend.
 */

export type ReviewTagTone = 'success' | 'info' | 'warn' | 'danger' | 'navy';

export interface ReviewTag {
  label: string;
  tone: ReviewTagTone;
}

/** Linha da tabela em árvore, na ordem de exibição. */
export interface ReviewRow {
  node: OrgChartProposalNodeResponse;
  level: number;
  /** Razão social da Receita; sem ela, o nome do documento. */
  name: string;
  /** Nome do documento quando difere da Receita (aparece embaixo do nome). */
  documentNameWhenDifferent: string | null;
  isPerson: boolean;
  /** Pessoa física ou CNPJ de outro cliente: não entra e a caixa não muda. */
  isLocked: boolean;
  /** NeedsCnpj ou InvalidCnpj: a pessoa digita ou corrige o CNPJ na linha. */
  editableCnpj: boolean;
  tag: ReviewTag;
  /** Avisos fixos da linha (outros grupos, nome diferente, raiz, mover no grupo). */
  warnings: string[];
}

export interface ReviewSummary {
  created: number;
  existing: number;
  attention: number;
  persons: number;
}

export interface ReviewIncludedCount {
  total: number;
  createdNow: number;
  moved: number;
  includedExisting: number;
  /** CNPJ digitado ou ainda não achado na Receita: o backend confere no apply. */
  checkedOnApply: number;
}

/** Escolhas da pessoa por `tempId`. */
export interface ReviewChoices {
  checked: Record<string, boolean>;
  typedCnpj: Record<string, string>;
}

export const NEEDS_CNPJ_MESSAGE = 'Sem CNPJ não entra. As empresas abaixo dela sobem um nível.';
export const REGISTERED_ELSEWHERE_MESSAGE =
  'Este CNPJ já está cadastrado em outro cliente do Cognito e não pode entrar aqui. Fale com o suporte se a empresa deveria estar neste cliente.';
export const INVALID_CNPJ_MESSAGE = 'Dígito verificador não confere. Corrija ou desmarque.';

export function onlyDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

/** CNPJ com 14 dígitos e dígitos verificadores certos (exibição; o backend confere de novo). */
export function isValidCnpj(value: string | null | undefined): boolean {
  const digits = onlyDigits(value);
  if (digits.length !== 14 || /^(\d)\1{13}$/.test(digits)) return false;

  const checkDigit = (base: string): number => {
    let weight = base.length - 7;
    let sum = 0;
    for (const char of base) {
      sum += Number(char) * weight;
      weight = weight === 2 ? 9 : weight - 1;
    }
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };

  const first = checkDigit(digits.slice(0, 12));
  const second = checkDigit(digits.slice(0, 12) + first);
  return digits.endsWith(`${first}${second}`);
}

export function formatCnpjForDisplay(value: string | null | undefined): string {
  const digits = onlyDigits(value);
  if (digits.length !== 14) return value || '—';
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

export function isPersonNode(node: OrgChartProposalNodeResponse): boolean {
  return node.action === OrgChartNodeActions.Person;
}

/** Nó que nunca entra no grupo: pessoa física ou CNPJ cadastrado em outro cliente. */
export function isLockedNode(node: OrgChartProposalNodeResponse): boolean {
  return isPersonNode(node) || node.action === OrgChartNodeActions.RegisteredElsewhere;
}

/**
 * Nome principal da linha: o do cadastro do tenant quando a empresa já existe (`existingCompanyName`), senão a razão
 * social da Receita, senão o nome do documento.
 */
export function resolveNodeName(node: OrgChartProposalNodeResponse): string {
  const registeredName =
    node.action === OrgChartNodeActions.UseExisting ? node.existingCompanyName : null;
  return registeredName || node.revenueName || node.documentName || 'Sem nome no documento';
}

export function isEditableCnpjNode(node: OrgChartProposalNodeResponse): boolean {
  return (
    node.action === OrgChartNodeActions.NeedsCnpj || node.action === OrgChartNodeActions.InvalidCnpj
  );
}

/** CNPJ que vai no apply: o digitado nas linhas editáveis, o lido nas demais. */
export function resolveNodeCnpj(
  node: OrgChartProposalNodeResponse,
  choices: ReviewChoices
): string {
  if (isEditableCnpjNode(node)) return onlyDigits(choices.typedCnpj[node.tempId]);
  return onlyDigits(node.cnpj);
}

/** Entra no grupo: marcado, não é pessoa e o CNPJ confere. */
export function isNodeIncluded(
  node: OrgChartProposalNodeResponse,
  choices: ReviewChoices
): boolean {
  if (isLockedNode(node) || !choices.checked[node.tempId]) return false;
  return isValidCnpj(resolveNodeCnpj(node, choices));
}

/** Erro do CNPJ da linha editável, só quando a linha está marcada. */
export function resolveCnpjError(
  node: OrgChartProposalNodeResponse,
  choices: ReviewChoices
): string | null {
  if (!isEditableCnpjNode(node) || !choices.checked[node.tempId]) return null;
  const digits = resolveNodeCnpj(node, choices);
  if (!digits) return NEEDS_CNPJ_MESSAGE;
  if (isValidCnpj(digits)) return null;
  return digits.length < 14 && node.action === OrgChartNodeActions.NeedsCnpj
    ? NEEDS_CNPJ_MESSAGE
    : INVALID_CNPJ_MESSAGE;
}

/** Escolhas iniciais: marca o que o backend marcou (`selected`), e o CNPJ lido nas linhas de CNPJ inválido. */
export function buildInitialChoices(proposal: OrgChartProposalResponse): ReviewChoices {
  const checked: Record<string, boolean> = {};
  const typedCnpj: Record<string, string> = {};
  for (const node of proposal.nodes) {
    checked[node.tempId] = !isLockedNode(node) && node.selected;
    if (isEditableCnpjNode(node)) typedCnpj[node.tempId] = onlyDigits(node.cnpjAsWritten);
  }
  return { checked, typedCnpj };
}

export function buildReviewTag(node: OrgChartProposalNodeResponse): ReviewTag {
  switch (node.action) {
    case OrgChartNodeActions.CreateNew:
      return { label: 'Cadastrar e incluir', tone: 'success' };
    case OrgChartNodeActions.UseExisting:
      return node.existingMemberId
        ? { label: 'Mover no grupo', tone: 'info' }
        : { label: 'Incluir (já cadastrada)', tone: 'info' };
    case OrgChartNodeActions.NeedsCnpj:
      return { label: 'Falta o CNPJ', tone: 'warn' };
    case OrgChartNodeActions.InvalidCnpj:
      return { label: 'CNPJ inválido', tone: 'danger' };
    case OrgChartNodeActions.NotFoundInRevenue:
      return { label: 'Não achado na Receita', tone: 'danger' };
    case OrgChartNodeActions.RegisteredElsewhere:
      return { label: 'Já cadastrada em outro cliente', tone: 'danger' };
    case OrgChartNodeActions.Person:
      return { label: 'Não entra', tone: 'navy' };
    default:
      return { label: 'Conferir', tone: 'warn' };
  }
}

function normalizeName(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();
}

function formatPercentage(value: number | null): string {
  if (value === null || value === undefined) return '';
  return ` (${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%)`;
}

function buildWarnings(
  node: OrgChartProposalNodeResponse,
  level: number,
  parent: OrgChartProposalNodeResponse | undefined,
  nameIsDifferent: boolean,
  isSuggestedPrimary: boolean
): string[] {
  const warnings: string[] = [];

  if (isPersonNode(node)) {
    warnings.push(
      level === 0
        ? 'Pessoa física no topo; só ajuda a montar a árvore'
        : 'Pessoa física; só ajuda a montar a árvore'
    );
    return warnings;
  }

  if (node.action === OrgChartNodeActions.RegisteredElsewhere) {
    warnings.push(REGISTERED_ELSEWHERE_MESSAGE);
    return warnings;
  }

  if (isSuggestedPrimary) {
    warnings.push(
      node.existingMemberId
        ? 'Já está neste grupo; sugerida como empresa principal'
        : 'Sugerida como empresa principal'
    );
  } else if (level === 0) {
    warnings.push('Raiz do organograma');
  }
  if (
    !isSuggestedPrimary &&
    node.action === OrgChartNodeActions.UseExisting &&
    node.existingMemberId
  ) {
    const parentName = parent ? resolveNodeName(parent) : null;
    warnings.push(
      parentName
        ? `Já está neste grupo; passa a ficar abaixo da ${parentName}${formatPercentage(node.ownershipPercentage)}`
        : 'Já está neste grupo; passa a ficar no topo'
    );
  }
  if (nameIsDifferent) {
    warnings.push(
      node.action === OrgChartNodeActions.UseExisting && node.existingCompanyName
        ? 'Nome no documento diferente do cadastro'
        : 'Nome no documento diferente da Receita'
    );
  }
  for (const group of node.otherGroups ?? []) {
    warnings.push(`Também está no grupo ${group.name}`);
  }
  if (node.action === OrgChartNodeActions.NotFoundInRevenue) {
    warnings.push(
      'A Receita não respondeu para este CNPJ; o cadastro consulta de novo ao aplicar.'
    );
  }
  return warnings;
}

/**
 * Pai que vale no grupo: sobe a árvore pulando quem nunca entra (pessoa física, CNPJ de outro cliente), porque os
 * filhos deles sobem para o ancestral incluído mais próximo no apply. Nenhum ancestral elegível = topo do grupo.
 */
function findEffectiveParent(
  node: OrgChartProposalNodeResponse,
  byId: Map<string, OrgChartProposalNodeResponse>
): OrgChartProposalNodeResponse | undefined {
  const seen = new Set<string>([node.tempId]);
  let current = node.parentTempId ? byId.get(node.parentTempId) : undefined;
  while (current && !seen.has(current.tempId)) {
    if (!isLockedNode(current)) return current;
    seen.add(current.tempId);
    current = current.parentTempId ? byId.get(current.parentTempId) : undefined;
  }
  return undefined;
}

/**
 * Linhas na ordem da árvore (pai antes dos filhos, em profundidade). Nó cujo pai não existe na proposta vira raiz;
 * nó que sobrar (ciclo) entra no fim, no nível 0.
 */
export function buildReviewRows(proposal: OrgChartProposalResponse): ReviewRow[] {
  const byId = new Map(proposal.nodes.map(node => [node.tempId, node]));
  const children = new Map<string | null, OrgChartProposalNodeResponse[]>();
  for (const node of proposal.nodes) {
    const parentId =
      node.parentTempId && byId.has(node.parentTempId) && node.parentTempId !== node.tempId
        ? node.parentTempId
        : null;
    const list = children.get(parentId) ?? [];
    list.push(node);
    children.set(parentId, list);
  }

  const rows: ReviewRow[] = [];
  const visited = new Set<string>();
  const visit = (node: OrgChartProposalNodeResponse, level: number): void => {
    if (visited.has(node.tempId)) return;
    visited.add(node.tempId);

    const name = resolveNodeName(node);
    const nameIsDifferent =
      !!node.documentName &&
      name !== node.documentName &&
      normalizeName(name) !== normalizeName(node.documentName);
    const parent = findEffectiveParent(node, byId);
    const isSuggestedPrimary = node.tempId === proposal.suggestedPrimaryTempId;

    rows.push({
      node,
      level,
      name,
      documentNameWhenDifferent: nameIsDifferent ? node.documentName : null,
      isPerson: isPersonNode(node),
      isLocked: isLockedNode(node),
      editableCnpj: isEditableCnpjNode(node),
      tag: buildReviewTag(node),
      warnings: buildWarnings(node, level, parent, nameIsDifferent, isSuggestedPrimary),
    });

    for (const child of children.get(node.tempId) ?? []) visit(child, level + 1);
  };

  for (const root of children.get(null) ?? []) visit(root, 0);
  for (const node of proposal.nodes) visit(node, 0);
  return rows;
}

export function buildReviewSummary(proposal: OrgChartProposalResponse): ReviewSummary {
  const summary: ReviewSummary = { created: 0, existing: 0, attention: 0, persons: 0 };
  for (const node of proposal.nodes) {
    switch (node.action) {
      case OrgChartNodeActions.CreateNew:
        summary.created++;
        break;
      case OrgChartNodeActions.UseExisting:
        summary.existing++;
        break;
      case OrgChartNodeActions.Person:
        summary.persons++;
        break;
      default:
        summary.attention++;
    }
  }
  return summary;
}

export function countIncluded(
  proposal: OrgChartProposalResponse,
  choices: ReviewChoices
): ReviewIncludedCount {
  const count: ReviewIncludedCount = {
    total: 0,
    createdNow: 0,
    moved: 0,
    includedExisting: 0,
    checkedOnApply: 0,
  };
  for (const node of proposal.nodes) {
    if (!isNodeIncluded(node, choices)) continue;
    count.total++;
    if (node.action === OrgChartNodeActions.CreateNew) count.createdNow++;
    else if (node.action === OrgChartNodeActions.UseExisting && node.existingMemberId)
      count.moved++;
    else if (node.action === OrgChartNodeActions.UseExisting) count.includedExisting++;
    else count.checkedOnApply++;
  }
  return count;
}

/** Texto do `title` do contador: "2 cadastradas agora, 1 movida, 3 incluídas". */
export function describeIncludedCount(count: ReviewIncludedCount): string {
  const parts = [
    `${count.createdNow} ${count.createdNow === 1 ? 'cadastrada' : 'cadastradas'} agora`,
    `${count.moved} ${count.moved === 1 ? 'movida' : 'movidas'}`,
    `${count.includedExisting} ${count.includedExisting === 1 ? 'incluída' : 'incluídas'}`,
  ];
  if (count.checkedOnApply) {
    parts.push(`${count.checkedOnApply} conferida(s) no cadastro ao aplicar`);
  }
  return parts.join(', ');
}

/** Nó sugerido como principal, quando ele entra no grupo. */
export function resolveSuggestedPrimary(
  proposal: OrgChartProposalResponse,
  choices: ReviewChoices
): OrgChartProposalNodeResponse | null {
  const id = proposal.suggestedPrimaryTempId;
  const node = id ? proposal.nodes.find(n => n.tempId === id) : undefined;
  return node && isNodeIncluded(node, choices) ? node : null;
}

/** Payload do apply com TODOS os nós (ausente conta como excluído no backend, mas mandamos explícito). */
export function buildApplyPayload(
  proposal: OrgChartProposalResponse,
  choices: ReviewChoices,
  setAsPrimary: boolean
): ApplyOrgChartImportRequest {
  const primary = resolveSuggestedPrimary(proposal, choices);
  return {
    nodes: proposal.nodes.map(node => ({
      tempId: node.tempId,
      include: isNodeIncluded(node, choices),
      cnpj: resolveNodeCnpj(node, choices) || null,
      parentTempId: node.parentTempId,
      ownershipPercentage: node.ownershipPercentage,
    })),
    primaryTempId: setAsPrimary && primary ? primary.tempId : null,
  };
}

/** Mensagem de negócio do backend (`errors[].message`); `null` para falha técnica. */
export function describeOrgChartImportError(error: unknown): string | null {
  if (!(error instanceof ErrorResponse)) return null;
  const item = error.errors?.find(e => !!e.message && e.key !== 'unknown');
  return item?.message ?? null;
}
